import { BROADCAST_OPTIONS } from '../broadcast/broadcast'

export type FeeBroadcaster = 'paymaster' | 'relayer'

export function increaseFee(amount: bigint, broadcaster: FeeBroadcaster = 'relayer'): bigint {
  if (broadcaster === 'paymaster') return amount + amount / 10n

  return amount + amount / 20n
}

function getAmountAfterFeeTokenConvert(
  simulatedGasLimit: bigint,
  gasPrice: bigint,
  nativeRatio: bigint,
  feeTokenDecimals: number,
  addedNative: bigint
) {
  const amountInWei = simulatedGasLimit * gasPrice + addedNative

  // Convert native gas cost to fee-token units, preserving 18 decimals of ratio precision.
  const extraDecimals = BigInt(10 ** 18)
  const feeTokenExtraDecimals = BigInt(10 ** (18 - feeTokenDecimals))
  const pow = extraDecimals * feeTokenExtraDecimals
  const result = (amountInWei * nativeRatio) / pow

  if (result === 0n && amountInWei !== 0n) {
    return 1n
  }

  return result
}

export function calculateFeeAmount({
  broadcastOption,
  simulatedGasLimit,
  gasPrice,
  nativeRatio,
  feeTokenDecimals,
  addedNative,
  usesPaymaster
}: {
  broadcastOption: string
  simulatedGasLimit: bigint
  gasPrice: bigint
  nativeRatio: bigint
  feeTokenDecimals: number
  addedNative: bigint
  usesPaymaster?: boolean
}): bigint {
  if (
    broadcastOption === BROADCAST_OPTIONS.bySelf ||
    broadcastOption === BROADCAST_OPTIONS.bySelf7702 ||
    broadcastOption === BROADCAST_OPTIONS.byOtherEOA
  ) {
    return simulatedGasLimit * gasPrice + addedNative
  }

  let amount = getAmountAfterFeeTokenConvert(
    simulatedGasLimit,
    gasPrice,
    nativeRatio,
    feeTokenDecimals,
    addedNative
  )

  if (broadcastOption === BROADCAST_OPTIONS.byBundler && usesPaymaster) {
    amount = increaseFee(amount, 'paymaster')
  } else if (broadcastOption !== BROADCAST_OPTIONS.byBundler) {
    amount = increaseFee(amount)
  }

  return amount
}

const isSelfBroadcast = (broadcastOption: string) =>
  broadcastOption === BROADCAST_OPTIONS.bySelf ||
  broadcastOption === BROADCAST_OPTIONS.bySelf7702 ||
  broadcastOption === BROADCAST_OPTIONS.byOtherEOA

/**
 * ZKX: the native a self-broadcast txn needs in the account before the node
 * accepts it. `calculateFeeAmount` prices the shown fee at the received gas
 * price, but the txn goes out with the speed-increased `maxFeePerGas`, and the
 * node checks `value + gasLimit * maxFeePerGas` (+ the L1 data fee on OP
 * stack chains) against the balance. Reserving only the shown fee left a Max
 * transfer short and the broadcast failed with "insufficient funds for the
 * transaction fee" (Trello #512). The L1 fee is an estimate that moves with L1
 * gas until the txn lands, so it gets a 25% margin.
 * For relayer / bundler broadcasts the fee amount is what gets charged.
 */
export function calculateMaxBroadcastCost({
  broadcastOption,
  simulatedGasLimit,
  gasPrice,
  addedNative,
  amount
}: {
  broadcastOption: string
  simulatedGasLimit: bigint
  gasPrice: bigint
  addedNative: bigint
  amount: bigint
}): bigint {
  if (!isSelfBroadcast(broadcastOption)) return amount

  const maxCost = simulatedGasLimit * gasPrice + addedNative + addedNative / 4n
  return maxCost > amount ? maxCost : amount
}

/**
 * The fee to hold back in the fee token: the max broadcast cost when known,
 * the shown fee otherwise (fee payments stored by older versions).
 */
export function getGasFeePaymentReserve(gasFeePayment: {
  amount: bigint
  maxCost?: bigint
}): bigint {
  const { amount, maxCost } = gasFeePayment
  return maxCost !== undefined && maxCost > amount ? maxCost : amount
}
